// src/pages/teacher/TeacherAssignments.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, Button, Badge, Form, InputGroup,
  Spinner, Table, Modal, Alert, ButtonGroup
} from "react-bootstrap";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  collection, query, where, onSnapshot, addDoc, doc,
  updateDoc, deleteDoc, getDoc, getDocs, limit, serverTimestamp
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";

/* ────────────────────────────────────────────────────────────
   Theme helpers
   ──────────────────────────────────────────────────────────── */
   
const cssVar = (name, fb = "") =>
  (typeof window !== "undefined" &&
    getComputedStyle(document.documentElement).getPropertyValue(name).trim()) || fb;

const useBrand = () => {
  const read = () => ({
    primary: cssVar("--brand-primary", "#0d6efd"),
    accent:  cssVar("--brand-accent",  "#6a8cff"),
    surface: cssVar("--surface", "#fff"),
  });
  const [brand, setBrand] = useState(read);
  useEffect(() => {
    const upd = () => setBrand(read());
    upd();
    const obs = new MutationObserver(upd);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme","style","class"] });
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    mq?.addEventListener?.("change", upd);
    return () => { obs.disconnect(); mq?.removeEventListener?.("change", upd); };
  }, []);
  return brand;
};

/* ──────────────────────────────────────────────────────────── */
const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const fmt = (d, f = "DD MMM YYYY") => (d && dayjs(d).isValid() ? dayjs(d).format(f) : "—");
const lower = (s) => String(s || "").trim().toLowerCase();

const SUBJECT_ICONS = {
  physics: "bi-lightning-charge", chemistry: "bi-beaker", biology: "bi-heart-pulse",
  maths: "bi-calculator", mathematics: "bi-calculator", english: "bi-book",
  computers: "bi-code-slash", "computer science": "bi-code-slash",
  economics: "bi-graph-up", commerce: "bi-cash-coin", default: "bi-journal-text",
};
const subjectIcon = (s) => SUBJECT_ICONS[lower(s)] || SUBJECT_ICONS.default;

const indexLink = (err) =>
  err?.message?.match(/https:\/\/console\.firebase\.google\.com\/[^\s)]+/i)?.[0] || "";

/* ────────────────────────────────────────────────────────────
   Create / Edit assignment modal — WITH BRANCH
   ──────────────────────────────────────────────────────────── */
function AssignmentFormModal({ show, onHide, onSave, initial, teacher }) {
  const isEdit = !!initial;
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [className, setClassName] = useState("");
  const [section, setSection] = useState("");
  const [branch, setBranch] = useState("");            // NEW
  const [givenAt, setGivenAt] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [instructions, setInstructions] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (isEdit) {
      setTitle(initial.title || "");
      setSubject(initial.subject || teacher?.primarySubject || "");
      setClassName(initial.className || teacher?.className || "");
      setSection(initial.section || (Array.isArray(initial.sections) ? initial.sections[0] : (teacher?.section || "")));
      setBranch(initial.branch || (Array.isArray(initial.branches) ? initial.branches[0] : (teacher?.branch || ""))); // NEW
      setGivenAt(initial.givenAt ? dayjs(toJS(initial.givenAt)).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"));
      setDueAt(initial.dueAt ? dayjs(toJS(initial.dueAt)).format("YYYY-MM-DD") : "");
      setInstructions(initial.instructions || initial.description || "");
      setFile(null);
    } else {
      setTitle("");
      setSubject(teacher?.primarySubject || "");
      setClassName(teacher?.activeClass || teacher?.className || ""); // harmless if not passed
setSection(teacher?.activeSectionU || teacher?.section || "");
      setBranch(teacher?.branch || "");     // NEW default
      setGivenAt(dayjs().format("YYYY-MM-DD"));
      setDueAt("");
      setInstructions("");
      setFile(null);
    }
  }, [show, isEdit, initial, teacher]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) return alert("Title is required.");
    if (!subject.trim()) return alert("Subject is required.");
    if (!className.trim()) return alert("Class is required.");
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        subject: subject.trim(),
        className: className.trim(),
        section: section.trim(),
        branch: branch.trim(),         // NEW
        givenAt: givenAt ? new Date(givenAt) : null,
        dueAt: dueAt ? new Date(dueAt) : null,
        instructions: instructions.trim(),
        file,
      });
      onHide?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit Assignment" : "New Assignment"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={7}>
              <Form.Group>
                <Form.Label>Title *</Form.Label>
                <Form.Control value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="Worksheet / Chapter" required />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Subject *</Form.Label>
                <Form.Control value={subject} onChange={(e)=>setSubject(e.target.value)} placeholder="Physics" required />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Class *</Form.Label>
                <Form.Control value={className} onChange={(e)=>setClassName(e.target.value)} placeholder="Class 12" required />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Section</Form.Label>
                <Form.Control value={section} onChange={(e)=>setSection(e.target.value)} placeholder="A" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Branch</Form.Label>
                <Form.Control value={branch} onChange={(e)=>setBranch(e.target.value)} placeholder="e.g., NEET / JEE / Science / Commerce" />
                <Form.Text className="text-muted">Leave blank to assign to all branches.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Given on</Form.Label>
                <Form.Control type="date" value={givenAt} onChange={(e)=>setGivenAt(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Due date</Form.Label>
                <Form.Control type="date" value={dueAt} onChange={(e)=>setDueAt(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Instructions</Form.Label>
                <Form.Control as="textarea" rows={4} value={instructions} onChange={(e)=>setInstructions(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Attach PDF / file (optional)</Form.Label>
                <Form.Control type="file" accept="application/pdf,image/*,.doc,.docx,.xlsx,.ppt,.pptx,.zip,.txt" onChange={(e)=>setFile(e.target.files?.[0] || null)} />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button className="btn-accent" type="submit" disabled={saving}>
            {saving ? (<><Spinner size="sm" animation="border" className="me-2" />Saving…</>) : "Save"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/* ────────────────────────────────────────────────────────────
   Submissions modal (read-only)
   ──────────────────────────────────────────────────────────── */
function SubmissionsModal({ show, onHide, schoolId, assignment }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    if (!show || !schoolId || !assignment?.id) return;
    setLoading(true);
    const col = collection(db, "schools", schoolId, "assignments", assignment.id, "submissions");
    const unsub = onSnapshot(col, (snap) => {
      setRows(snap.docs.map(d => ({ id: d.id, ...(d.data()||{}) })));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [show, schoolId, assignment?.id]);

  const submittedCount = rows.filter(r => (r.status||"") === "completed").length;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Submissions • {assignment?.title || "Assignment"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-2">
          <Badge bg="light" text="dark" className="border">
            {submittedCount} submitted / {rows.length} total
          </Badge>
        </div>
        {loading ? (
          <div className="text-muted"><Spinner size="sm" animation="border" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-muted">No submissions yet.</div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Student</th>
                  <th className="d-none d-md-table-cell">Status</th>
                  <th className="d-none d-md-table-cell">Submitted</th>
                  <th>File</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td className="fw-semibold">
                      {r.byStudent?.name || "—"}
                      {r.byStudent?.rollNo ? <span className="text-muted ms-2">({r.byStudent.rollNo})</span> : null}
                    </td>
                    <td className="d-none d-md-table-cell">
                      <Badge bg={r.status === "completed" ? "success" : r.status === "pending" ? "secondary" : "light"} text={r.status ? undefined : "dark"}>
                        {(r.status || "—").toUpperCase()}
                      </Badge>
                    </td>
                    <td className="d-none d-md-table-cell">{r.submittedAt ? dayjs(toJS(r.submittedAt)).format("DD MMM YYYY, hh:mm A") : "—"}</td>
                    <td>
                      {r.fileURL ? (
                        <Button size="sm" variant="outline-success" onClick={()=>window.open(r.fileURL, "_blank")}>
                          <i className="bi bi-box-arrow-up-right me-1" />
                          Open {r.fileName ? `(${r.fileName})` : ""}
                        </Button>
                      ) : <span className="text-muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

/* ────────────────────────────────────────────────────────────
   Main page — WITH CLASS/SECTION/BRANCH MAPPING
   ──────────────────────────────────────────────────────────── */
export default function TeacherAssignments() {
  const { user, profile } = useAuth();
  useBrand(); // ensure CSS vars react to dark/light
  const schoolId = profile?.schoolId || null;

  const [teacher, setTeacher] = useState(null);
  const [loadingTeacher, setLoadingTeacher] = useState(true);
  const [teacherMsg, setTeacherMsg] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [qText, setQText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [onlyUpcoming, setOnlyUpcoming] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSubs, setShowSubs] = useState(false);
  const [subsFor, setSubsFor] = useState(null);

  const [idxUrl, setIdxUrl] = useState("");

  /* Load teacher profile (by id or email) */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingTeacher(true);
        setTeacherMsg("");
        if (!schoolId || !user?.email) { setTeacherMsg("Missing school/account."); return; }

        if (profile?.teacherId) {
          const snap = await getDoc(doc(db, "schools", schoolId, "teachers", profile.teacherId));
          if (alive && snap.exists()) { setTeacher({ id: snap.id, ...(snap.data()||{}) }); setLoadingTeacher(false); return; }
        }
        const qs = await getDocs(query(
          collection(db, "schools", schoolId, "teachers"),
          where("email", "==", (user.email||"").toLowerCase()),
          limit(1)
        ));
        if (alive) {
          if (!qs.empty) setTeacher({ id: qs.docs[0].id, ...(qs.docs[0].data()||{}) });
          else setTeacherMsg("Teacher record not found for this account.");
          setLoadingTeacher(false);
        }
      } catch {
        if (alive) { setTeacherMsg("Failed to load teacher profile."); setLoadingTeacher(false); }
      }
    })();
    return () => { alive = false; };
  }, [schoolId, user?.email, profile?.teacherId]);

  const teacherName = teacher?.name || user?.displayName || user?.email || "Teacher";
  const classTag = [teacher?.className, teacher?.section].filter(Boolean).join("-") || "—";
  const primarySubject = (teacher?.primarySubject || "").trim();
  const teacherBranch = (teacher?.branch || "").trim();                // NEW
// --- Multi-class / multi-section support ---
const parseSections = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map(s=>s.trim()).filter(Boolean).map(s=>s.toUpperCase());
  return String(raw).split(/[,\s/|]+/g).map(s=>s.trim()).filter(Boolean).map(s=>s.toUpperCase());
};

const teacherClasses = useMemo(
  () =>
    Array.isArray(teacher?.classIds) && teacher.classIds.length
      ? teacher.classIds.filter(Boolean)
      : (teacher?.className ? [teacher.className] : []),
  [teacher]
);
const teacherSectionsU = useMemo(
  () => parseSections(teacher?.sectionIds ?? teacher?.sections ?? teacher?.section),
  [teacher]
);

// active selection for this page
const [activeClass, setActiveClass] = useState("");
const [activeSectionU, setActiveSectionU] = useState(""); // "" = all sections

useEffect(() => {
  if (!teacherClasses.length) { setActiveClass(""); return; }
  setActiveClass((prev) => (teacherClasses.includes(prev) ? prev : teacherClasses[0]));
}, [teacherClasses]);

useEffect(() => {
  setActiveSectionU((prev) => (prev && !teacherSectionsU.includes(prev) ? "" : prev));
}, [teacherSectionsU]);

  /* Subscribe: teacher’s subject + class + (optional) section/branch */
useEffect(() => {
  if (!schoolId || !activeClass || !primarySubject) {
    setRows([]); setLoading(false); return;
  }
  setLoading(true);
  const col = collection(db, "schools", schoolId, "assignments");
  const bucket = new Map();
  const merge = (docs) => {
    docs.forEach((d) => bucket.set(d.id, { id: d.id, ...(d.data()||{}) }));
    setRows(Array.from(bucket.values()));
    setLoading(false);
  };
  const unsubs = [];

  // base: class + subject (no section/branch constraint)
  unsubs.push(onSnapshot(
    query(col, where("className", "==", activeClass), where("subject", "==", primarySubject)),
    (snap) => merge(snap.docs),
    (err) => setIdxUrl(indexLink(err))
  ));

  // sections: if a specific one is selected -> subscribe that section and "all"
  if (activeSectionU) {
    try {
      unsubs.push(onSnapshot(
        query(
          col,
          where("className", "==", activeClass),
          where("subject", "==", primarySubject),
          where("sections", "array-contains", activeSectionU)
        ),
        (snap) => merge(snap.docs),
        (err) => setIdxUrl(indexLink(err))
      ));
      // single-field section support
      unsubs.push(onSnapshot(
        query(
          col,
          where("className", "==", activeClass),
          where("subject", "==", primarySubject),
          where("section", "==", activeSectionU)
        ),
        (snap) => merge(snap.docs),
        (err) => setIdxUrl(indexLink(err))
      ));
    } catch {}
  } else {
    // All Sections: pull teacher's sections + "all" (empty). If none defined, just rely on base query.
    const secList = teacherSectionsU.length ? teacherSectionsU : [];
    secList.forEach((sec) => {
      try {
        unsubs.push(onSnapshot(
          query(
            col,
            where("className", "==", activeClass),
            where("subject", "==", primarySubject),
            where("sections", "array-contains", sec)
          ),
          (snap) => merge(snap.docs),
          (err) => setIdxUrl(indexLink(err))
        ));
        unsubs.push(onSnapshot(
          query(
            col,
            where("className", "==", activeClass),
            where("subject", "==", primarySubject),
            where("section", "==", sec)
          ),
          (snap) => merge(snap.docs),
          (err) => setIdxUrl(indexLink(err))
        ));
      } catch {}
    });
    // rows meant for all sections: section == "" (single-field)
    try {
      unsubs.push(onSnapshot(
        query(
          col,
          where("className", "==", activeClass),
          where("subject", "==", primarySubject),
          where("section", "==", "")
        ),
        (snap) => merge(snap.docs),
        (err) => setIdxUrl(indexLink(err))
      ));
    } catch {}
  }

  // branch targeting (optional)
  if (teacherBranch) {
    try {
      unsubs.push(onSnapshot(
        query(
          col,
          where("className", "==", activeClass),
          where("subject", "==", primarySubject),
          where("branches", "array-contains", teacherBranch)
        ),
        (snap) => merge(snap.docs),
        (err) => setIdxUrl(indexLink(err))
      ));
    } catch {}
    try {
      unsubs.push(onSnapshot(
        query(
          col,
          where("className", "==", activeClass),
          where("subject", "==", primarySubject),
          where("branch", "in", [teacherBranch, "all", ""])
        ),
        (snap) => merge(snap.docs),
        (err) => setIdxUrl(indexLink(err))
      ));
    } catch {}
  }

  return () => unsubs.forEach((u)=>u && u());
}, [schoolId, activeClass, activeSectionU, teacherSectionsU.join("|"), primarySubject, teacherBranch]);


  /* Helpers */
  const matchesBranchForTeacher = (r) => {
    const tb = lower(teacherBranch);
    if (!tb) return true;                             // teacher doesn’t use branches
    const list = Array.isArray(r.branches) ? r.branches : (r.branch ? [r.branch] : []);
    if (list.length === 0) return true;              // treat empty as ALL
    const has = list.map(lower).some(v => v === tb || v === "all");
    return has || r.teacherId === teacher?.id;       // always show own items
  };

  const filtered = useMemo(() => {
    const now = new Date();
    const q = lower(qText);
    return rows
      .filter(matchesBranchForTeacher)                // NEW
     .filter(r => {
  // ---- CLASS match (if a class is selected) ----
  if (activeClass && r.className !== activeClass) return false;

  // ---- SECTION match ----
  const secActive = String(activeSectionU || "").toUpperCase();
  const secTeacherList = (teacherSectionsU || []).map(s => String(s).toUpperCase());

  const rSecSingle = String(r.section || "").toUpperCase(); // single-field
  const rSecList   = Array.isArray(r.sections) ? r.sections.map(s => String(s).toUpperCase()) : []; // array-field

  const rowTargetsAllSections =
    rSecSingle === "" || rSecSingle === "ALL" || rSecList.includes("ALL");

  if (secActive) {
    // When a specific section is selected, accept explicit match or "all sections"
    const matchesActive =
      rowTargetsAllSections ||
      rSecSingle === secActive ||
      rSecList.includes(secActive);

    if (!matchesActive) return false;
  } else if (secTeacherList.length) {
    // Viewing "All Sections" but teacher has mapped sections:
    const matchesAnyOfMine =
      rowTargetsAllSections ||
      secTeacherList.includes(rSecSingle) ||
      rSecList.some(s => secTeacherList.includes(s));

    if (!matchesAnyOfMine) return false;
  }
  // If teacher has no section mapping, class match is enough.

  // ---- BRANCH match (optional) ----
  const tBranch = (teacherBranch || "").trim().toLowerCase();
  if (tBranch) {
    const rBranches =
      Array.isArray(r.branches) && r.branches.length
        ? r.branches.map(lower)
        : (r.branch ? [lower(r.branch)] : []);

    // If the assignment specifies branches, require a match (or "all")
    if (rBranches.length && !rBranches.includes(tBranch) && !rBranches.includes("all")) {
      return false;
    }
  }

  // ---- STATUS & DATE logic (unchanged) ----
  const due = toJS(r.dueAt);
  const upcoming = due && dayjs(due).isAfter(now);
  const overdue = due && dayjs(due).isBefore(now) && !r.closed;

  if (onlyUpcoming && !upcoming) return false;

  if (statusFilter !== "all") {
    if (statusFilter === "upcoming" && !upcoming) return false;
    if (statusFilter === "overdue" && !overdue) return false;
    if (statusFilter === "closed" && !r.closed) return false;
  }

  if (fromDate) {
    const from = dayjs(fromDate).startOf("day");
    if (!due || dayjs(due).isBefore(from)) return false;
  }
  if (toDate) {
    const to = dayjs(toDate).endOf("day");
    if (!due || dayjs(due).isAfter(to)) return false;
  }

  if (onlyMine && teacher?.id && r.teacherId && r.teacherId !== teacher.id) return false;

  // ---- TEXT search (unchanged) ----
  if (q) {
    const hay = [
      r.title, r.subject, r.instructions, r.description,
      r.className, r.section, ...(Array.isArray(r.sections) ? r.sections : []),
      r.branch, ...(Array.isArray(r.branches) ? r.branches : []),
      r.teacherName, r.teacherEmail
    ].map(x => lower(x)).join(" ");
    if (!hay.includes(q)) return false;
  }

  return true;
})

      .sort((a,b)=>{
        const ad = toJS(a.dueAt) || new Date(8640000000000000);
        const bd = toJS(b.dueAt) || new Date(8640000000000000);
        if (ad.getTime() !== bd.getTime()) return ad - bd;
        const ag = toJS(a.givenAt || a.createdAt) || 0;
        const bg = toJS(b.givenAt || b.createdAt) || 0;
        return bg - ag;
      });
  }, [rows, qText, statusFilter, fromDate, toDate, onlyUpcoming, onlyMine, teacher?.id, teacher?.section, teacherBranch,activeClass,activeSectionU,matchesBranchForTeacher,teacherSectionsU,]);

  const isOverdue = (r) => {
    const d = toJS(r.dueAt);
    return d && dayjs(d).isBefore(new Date()) && !r.closed;
  };

  /* CRUD (save with BRANCH fields) */
  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit   = (row) => { setEditing(row); setShowForm(true); };

  const saveAssignment = async (p) => {
    if (!schoolId || !teacher) throw new Error("Missing context.");
    const base = {
      title: p.title,
      subject: p.subject,
className: p.className || activeClass,
      section: (p.section || activeSectionU || "").toUpperCase(),
      sections: (p.section || activeSectionU) ? [String(p.section || activeSectionU).toUpperCase()] : [],
      branch: p.branch || "",                 // NEW
      branches: p.branch ? [p.branch] : [],   // NEW
      givenAt: p.givenAt || serverTimestamp(),
      dueAt: p.dueAt || null,
      instructions: p.instructions || "",
      teacherId: teacher.id || "",
      teacherName: teacher.name || "",
      teacherEmail: (teacher.email || "").toLowerCase(),
      createdBy: { uid: user?.uid || "", email: (user?.email||"").toLowerCase() },
      updatedAt: serverTimestamp(),
      closed: false,
    };

    if (editing) {
      const refDoc = doc(db, "schools", schoolId, "assignments", editing.id);
      let fileURL = editing.fileURL || "";
      let fileName = editing.fileName || "";
      let filePath = editing.filePath || "";
      if (p.file) {
        try { if (filePath) await deleteObject(sRef(storage, filePath)); } catch {}
        const safe = `${Date.now()}_${p.file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
        filePath = `schools/${schoolId}/assignments/${editing.id}/${safe}`;
        await uploadBytes(sRef(storage, filePath), p.file);
        fileURL = await getDownloadURL(sRef(storage, filePath));
        fileName = p.file.name;
      }
      await updateDoc(refDoc, { ...base, fileURL, fileName, filePath });
    } else {
      const newRef = await addDoc(collection(db, "schools", schoolId, "assignments"), { ...base, createdAt: serverTimestamp() });
      if (p.file) {
        const safe = `${Date.now()}_${p.file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
        const filePath = `schools/${schoolId}/assignments/${newRef.id}/${safe}`;
        await uploadBytes(sRef(storage, filePath), p.file);
        const fileURL = await getDownloadURL(sRef(storage, filePath));
        await updateDoc(newRef, { fileURL, fileName: p.file.name, filePath });
      }
    }
  };

  const removeAssignment = async (row) => {
    if (!schoolId) return;
    if (!window.confirm(`Delete assignment "${row.title}"?`)) return;
    try {
      if (row.filePath) { try { await deleteObject(sRef(storage, row.filePath)); } catch {} }
      await deleteDoc(doc(db, "schools", schoolId, "assignments", row.id));
    } catch { alert("Failed to delete."); }
  };

  const exportCSV = () => {
    const data = filtered.map(r => ({
      Title: r.title || "",
      Subject: r.subject || "",
      Class: r.className || "",
      Section: r.section || (Array.isArray(r.sections) ? r.sections.join(",") : ""),
      Branch: r.branch || (Array.isArray(r.branches) ? r.branches.join(",") : ""), // NEW
      GivenOn: r.givenAt ? dayjs(toJS(r.givenAt)).format("YYYY-MM-DD") : "",
      DueOn: r.dueAt ? dayjs(toJS(r.dueAt)).format("YYYY-MM-DD") : "",
      Teacher: r.teacherName || r.teacherEmail || "",
      FileURL: r.fileURL || "",
      Closed: !!r.closed,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "assignments");
    XLSX.writeFile(wb, "teacher_assignments.csv");
  };

  const openSubmissions = (row) => { setSubsFor(row); setShowSubs(true); };
  const openFile = (u) => u && window.open(u, "_blank", "noopener,noreferrer");

  const totalCount = rows.length;
  const shownCount = filtered.length;
const isTeacherInactive = (teacher, profile) => {
  const s = String(teacher?.status ?? profile?.status ?? "")
    .trim()
    .toLowerCase();
  const hasInactiveFlag =
    typeof teacher?.active === "boolean" ? !teacher.active
    : typeof profile?.active === "boolean" ? !profile.active
    : false;

  return hasInactiveFlag || ["inactive", "disabled", "blocked"].includes(s);
};
const inactive = isTeacherInactive(teacher, profile);
// (optional) stop subscriptions when inactive
useEffect(() => {
  if (inactive) { setRows([]); setLoading(false); }
}, [inactive]);
// EARLY EXIT UI for inactive users
if (!loadingTeacher && teacher && inactive) {
  return (
    <Container fluid className="p-4">
      <Alert variant="danger" className="mb-3">
        <div className="fw-semibold mb-1">Access restricted</div>
        Your teacher account is marked as <b>Inactive</b>. Please contact the administrator to regain access.
      </Alert>
      <div className="d-flex gap-2">
        <Button variant="secondary" onClick={() => window.location.assign("/")}>
          Go Home
        </Button>
        <Button variant="outline-secondary" onClick={() => window.location.assign("/support")}>
          Contact Support
        </Button>
      </div>
    </Container>
  );
}
  return (
    <Container fluid className="p-0">
      {/* Header */}
      <div className="ta-hero mb-3">
        <div className="ta-hero-inner">
          <div>
            <div className="tdb-eyebrow">Teacher • Assignments</div>
            <h5 className="mb-1 fw-bold">{teacherName}</h5>
            <div className="d-flex flex-wrap align-items-center gap-2 text-muted">
              <div className="d-flex gap-2">
    <Form.Select
      size="sm"
      style={{ width: 180 }}
      value={activeClass}
      onChange={(e)=>setActiveClass(e.target.value)}
    >
      {teacherClasses.length === 0 ? <option value="">No class</option> : null}
      {teacherClasses.map(c => <option key={c} value={c}>{` ${c}`}</option>)}
    </Form.Select>

    <Form.Select
      size="sm"
      style={{ width: 180 }}
      value={activeSectionU}
      onChange={(e)=>setActiveSectionU(e.target.value)}
    >
      <option value="">All Sections</option>
      {teacherSectionsU.map(s => <option key={s} value={s}>{`Section ${s}`}</option>)}
    </Form.Select>
  </div>
              {primarySubject ? <Badge bg="light" text="dark">{primarySubject}</Badge> : null}
              {teacherBranch ? <Badge bg="light" text="dark">Branch {teacherBranch}</Badge> : null}
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={exportCSV}>
              <i className="bi bi-download me-1" /> Export CSV
            </Button>
            <Button className="btn-accent" onClick={openCreate}>
              <i className="bi bi-plus-lg me-1" /> New Assignment
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={6}>
              <Form.Label className="mb-1">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  placeholder="Title / subject / instructions / teacher / branch"
                  value={qText}
                  onChange={(e)=>setQText(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Label className="mb-1">Status</Form.Label>
              <Form.Select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="upcoming">Upcoming</option>
                <option value="overdue">Overdue</option>
                <option value="closed">Closed</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="mb-1">Due from</Form.Label>
              <Form.Control type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="mb-1">Due to</Form.Label>
              <Form.Control type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} />
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xs="auto" className="d-flex align-items-center gap-3">
              <Form.Check type="switch" id="only-upcoming" label="Show only upcoming" checked={onlyUpcoming} onChange={(e)=>setOnlyUpcoming(e.target.checked)} />
              <Form.Check type="switch" id="only-mine" label="Only mine" checked={onlyMine} onChange={(e)=>setOnlyMine(e.target.checked)} />
            </Col>
            <Col className="text-end">
              <Button variant="outline-secondary" onClick={()=>{ setQText(""); setStatusFilter("all"); setFromDate(""); setToDate(""); setOnlyUpcoming(false); setOnlyMine(false); }}>
                Reset
              </Button>
            </Col>
          </Row>

          {idxUrl ? (
            <Alert variant="info" className="mt-3 py-2">
              A Firestore composite index may be required for some filters. Create it here:{" "}
              <a href={idxUrl} target="_blank" rel="noreferrer">open index link</a>
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      {/* Assignment Board (scrollable) */}
      {loadingTeacher ? (
        <Card className="border-0 shadow-sm"><Card.Body><Spinner size="sm" animation="border" /> Loading profile…</Card.Body></Card>
      ) : teacherMsg ? (
        <Alert variant="warning" className="mb-0">{teacherMsg}</Alert>
      ) : loading ? (
        <Card className="border-0 shadow-sm"><Card.Body><Spinner size="sm" animation="border" /> Loading…</Card.Body></Card>
      ) : shownCount === 0 ? (
        <Card className="border-0 shadow-sm"><Card.Body className="text-muted text-center">No assignments found</Card.Body></Card>
      ) : (
        <Card className="assignment-board border-0">
          <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
            <div className="fw-semibold">Assignments</div>
            <Badge bg="light" text="dark" className="border">{shownCount} / {totalCount}</Badge>
          </Card.Header>
          <Card.Body className="p-2">
            <div className="assignments-scroll">
              <div className="assignments-grid">
                {filtered.map(r => (
                  <div key={r.id} className={`assignment-card ${isOverdue(r) ? "overdue" : ""}`}>
                    <div className="card-particles" aria-hidden />
                    <div className="assignment-card__body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center gap-3">
                          <div className="subj-ico d-inline-flex align-items-center justify-content-center" title={r.subject || "Subject"}>
                            <i className={`bi ${subjectIcon(r.subject)}`} />
                          </div>
                          <div>
                            <div className="fw-semibold text-truncate" title={r.title || "Assignment"}>{r.title || "Assignment"}</div>
                            <div className="text-muted small">{r.subject || "—"}</div>
                          </div>
                        </div>
                        <div>{isOverdue(r) ? <Badge bg="danger">Overdue</Badge> : <Badge bg="success">Live</Badge>}</div>
                      </div>

                      <div className="small">
                        Given: <b>{fmt(toJS(r.givenAt || r.createdAt))}</b>
                        {r.dueAt ? <> • Due: <b>{fmt(toJS(r.dueAt))}</b></> : null}
                      </div>

                      {r.instructions ? (
                        <div className="small mt-2 text-truncate" title={r.instructions}>{r.instructions}</div>
                      ) : null}

                      <div className="mt-auto pt-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex flex-wrap gap-2">
                          <Badge bg="light" text="dark" className="border">
                            {[r.className, r.section || (Array.isArray(r.sections) ? r.sections.join(",") : "")].filter(Boolean).join("-")}

                          </Badge>
                          {(r.branch || (Array.isArray(r.branches) && r.branches.length)) ? (
                            <Badge bg="light" text="dark" className="border">
                              Branch {r.branch || r.branches?.join(",")}
                            </Badge>
                          ) : null}
                        </div>
                        <ButtonGroup>
                          {r.fileURL ? (
                            <Button size="sm" variant="outline-success" onClick={()=>openFile(r.fileURL)} title="Open attached file">
                              <i className="bi bi-file-earmark-text" />
                            </Button>
                          ) : null}
                          <Button size="sm" variant="outline-secondary" onClick={()=>openSubmissions(r)} title="View submissions">
                            <i className="bi bi-people" />
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={()=>openEdit(r)} title="Edit">
                            <i className="bi bi-pencil" />
                          </Button>
                          <Button size="sm" variant="outline-danger" onClick={()=>removeAssignment(r)} title="Delete">
                            <i className="bi bi-trash" />
                          </Button>
                        </ButtonGroup>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Modals */}
      <AssignmentFormModal
        show={showForm}
        onHide={()=>{ setShowForm(false); setEditing(null); }}
        onSave={saveAssignment}
        initial={editing}
        teacher={{ ...teacher, activeClass, activeSectionU }}
      />
      <SubmissionsModal
        show={showSubs}
        onHide={()=>{ setShowSubs(false); setSubsFor(null); }}
        schoolId={schoolId}
        assignment={subsFor}
      />

      {/* Local styles (dark/light + accent) */}
      <style>{`
        .ta-hero {
          border: 1px solid var(--border);
          border-radius: 16px;
          background:
            radial-gradient(60% 120% at 10% -20%, color-mix(in oklab, var(--brand-accent) 16%, transparent) 0%, transparent 60%),
            radial-gradient(40% 120% at 110% 10%, color-mix(in oklab, var(--brand-accent) 10%, transparent) 0%, transparent 60%),
            color-mix(in oklab, var(--surface) 96%, transparent);
        }
        .ta-hero-inner { display:flex; align-items:center; justify-content:space-between; padding: 14px 16px; border-radius: 16px; }
        .tdb-eyebrow { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--muted); }

        .assignment-board { border-radius: 16px; }
        .assignments-scroll{
          max-height: calc(100vh - 320px);
          overflow: auto;
          padding: 6px;
          border-radius: 12px;
          background: color-mix(in oklab, var(--surface) 98%, transparent);
          border: 1px solid var(--border);
        }
        /* Smooth inertial scrolling on iOS */
        .assignments-scroll { -webkit-overflow-scrolling: touch; }

        /* Subtle scrollbar styled by accent */
        .assignments-scroll::-webkit-scrollbar{ height: 10px; width: 10px; }
        .assignments-scroll::-webkit-scrollbar-thumb{
          background: color-mix(in oklab, var(--brand-accent) 30%, transparent);
          border-radius: 8px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }

        .assignments-grid{
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 14px;
        }

        .assignment-card {
          position: relative;
          border-radius: 16px;
          padding: 14px;
          border: 1px solid var(--border);
          background:
            linear-gradient(180deg, color-mix(in oklab, var(--brand-accent) 6%, transparent) 0%, transparent 60%),
            color-mix(in oklab, var(--surface) 96%, transparent);
          /* Accent-based shadow (works in dark & light) */
          box-shadow: 0 .6rem 1.35rem color-mix(in oklab, var(--brand-accent) 22%, transparent);
          transition: transform .18s ease, box-shadow .18s ease, background .2s ease, border-color .2s ease;
        }
        .assignment-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 .75rem 1.6rem color-mix(in oklab, var(--brand-accent) 32%, transparent);
        }
        .assignment-card.overdue {
          box-shadow: 0 .55rem 1.3rem color-mix(in oklab, #dc3545 28%, transparent);
          border-color: color-mix(in oklab, #dc3545 35%, var(--border));
        }
        .assignment-card__body { display:flex; flex-direction:column; min-height: 180px; }

        .subj-ico {
          width: 54px; height: 54px; border-radius: 14px;
          background: color-mix(in oklab, var(--brand-accent) 16%, transparent);
          color: var(--brand-accent);
          border: 1px solid color-mix(in oklab, var(--brand-accent) 36%, transparent);
          font-size: 26px;
        }
        .card-particles {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image:
            radial-gradient(color-mix(in oklab, var(--brand-primary) 18%, transparent) 1px, transparent 1px),
            radial-gradient(color-mix(in oklab, var(--brand-accent) 18%, transparent) 1px, transparent 1px);
          background-position: 0 0, 12px 12px; background-size: 24px 24px, 24px 24px;
          animation: drift 9s linear infinite; opacity: .20;
        }
        @keyframes drift {
          0% { background-position: 0 0, 12px 12px; }
          50% { background-position: 12px 6px, 0 18px; }
          100% { background-position: 0 0, 12px 12px; }
        }
        .btn-accent {
          --accent: var(--brand-accent);
          background-color: var(--accent) !important;
          border-color: var(--accent) !important;
          color: #fff !important;
          box-shadow: 0 .25rem .6rem color-mix(in oklab, var(--brand-accent) 35%, transparent);
        }
        .btn-accent:hover, .btn-accent:focus { filter: brightness(0.92); color: #fff !important; }

        @media (max-width: 992px){
          .assignments-scroll{ max-height: calc(100vh - 360px); }
          .assignments-grid{ grid-template-columns: 1fr; }
        }
      `}</style>
    </Container>
  );
}
